import { useEffect, useState } from "react";
import {
  listBeatTimeline,
  type BeatTimelineEntryResponse,
} from "../../api/beats";
import { ApiError } from "../../api/client";

/**
 * scene-beat-decomposition (Phase 5+ Tier 5G 第 1 段) refactor:
 * BeatTimelineView / CurrentBeatIndicator / ScenarioBeatManagementView の 3 consumer が
 * 各々持っていた fetch 経路を統一する shared hook。 sibling primitive (= useToggle /
 * useDialogKeyboard / useOnline 等) と同 doctrine、 placement-agnostic な data hook 配備。
 *
 * fetch 経路:
 *   1. mount 時 / scenarioId / refreshKey 変更時に listBeatTimeline を呼出
 *   2. 成功で entries に populate、 ApiError / 例外で error に populate
 *   3. cancelled flag で stale fetch を破棄 (= React 18 StrictMode 配慮)
 *
 * 戻り値:
 *   - entries: 成功時 BeatTimelineEntryResponse[]、 未完了 / 失敗時 null
 *   - error: ApiError detail 文字列 / fallback 「ネットワークエラーが発生しました」
 *   - isLoading: fetch 中 boolean
 */
export interface UseBeatTimelineResult {
  entries: BeatTimelineEntryResponse[] | null;
  error: string | null;
  isLoading: boolean;
}

export function useBeatTimeline(
  baseUrl: string,
  scenarioId: string,
  refreshKey?: number,
): UseBeatTimelineResult {
  const [entries, setEntries] = useState<BeatTimelineEntryResponse[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    listBeatTimeline(baseUrl, scenarioId)
      .then((response) => {
        if (cancelled) return;
        setEntries(response.timeline);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError) {
          setError(err.problem.detail ?? err.problem.title ?? "取得に失敗しました");
        } else {
          setError("ネットワークエラーが発生しました");
        }
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [baseUrl, scenarioId, refreshKey]);

  return { entries, error, isLoading };
}
